// @flow
import React from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { OrderedMap } from 'immutable';
import { graphql } from 'react-apollo';
import { isEqual } from 'lodash';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import idx from 'idx';

import {
  flush,
  selectInitialTargets,
  loadGroups,
  loadOrganizations,
  loadGroupChats,
  loadIndividuals,
  loadPotentialChatMembers,
  searchGroupChats,
  clearSelection,
  RECIPIENT_TYPES,
} from 'reducers/roleBasedMessagingSearch';
import { MODAL_FILE_CONTEXT_KEY, removeFile } from 'reducers/fileUploads';
import Chats from 'collections/Chats';
import ChatStore from 'chat/stores/ChatStore';
import COMPOSER_GROUPS_QUERY from 'chat/queries/ComposerGroupsQuery.gql';
import COMPOSER_ORGS_QUERY from 'chat/queries/ComposerOrgsQuery.gql';
import { Message } from 'entities/Message';
import PotentialChatMember from 'entities/PotentialChatMember';
import ComposeMessage from './ComposeMessage';
import SelectRecipientPanel from './SelectRecipientPanel';
import SplitComposerHeader from './SplitComposerHeader';
import { ComposerContext } from './ComposerContext';

import type { Target } from 'entities/Message';
import type {
  Recipient,
  ComposerOrganization,
  ComposerGroup,
} from 'reducers/roleBasedMessagingSearch';
import type { UploadRequestData } from 'reducers/fileUploads';
import type { SelectRecipientTabs } from './SelectRecipientPanel';

export type MessageType = 'urgent' | 'message';

export type GeneratedProps = {
  groupsLoading: boolean,
  groups: ?(ComposerGroup[]),

  // graphQL
  orgsLoading: boolean,
  organizations: ?($Shape<ComposerOrganization>[]),
  schools: ?($Shape<ComposerOrganization>[]),

  // redux dispatchers
  onFlush: () => *,
  clearSelection: () => *,
  removeFile: (fileContextKey: string, filename: string) => void,
  onSelectInitialTargets: (targets: Target[]) => *,
  onLoadOrganizations: (organizations: ComposerOrganization[]) => *,
  onLoadGroups: (groups: ComposerGroup[]) => *,
  onLoadGroupChats: (chats: Chats) => *,
  onLoadIndividuals: (members: PotentialChatMember[]) => *,
  onLoadPotentialChatMembers: (query: string) => *,
  onSearchGroupChats: (query: string) => *,

  // redux state
  savedMessage: string,
  fileUploads: OrderedMap<string, UploadRequestData>,
  selection: Recipient[],

  loadingGroupChats: boolean,
  loadingIndividualRecipients: boolean,
};

export type RequiredProps = {
  defaultTab?: SelectRecipientTabs,
  draftAnnouncement?: ?Message,
  initialTargets?: Target[],
  onSignificantChangeMade?: ?Function,
  isEditingScheduledMessage?: boolean,
  onRequestHide?: () => void,
  onDismiss?: Function,
  skipSelectRecipients: boolean,
  onMessageSent?: Function,
  onPageChange?: ('share_to' | 'composer') => void,
  messageType: MessageType,

  // sharesdk
  shareSdkBodyUrl: ?string,
};

type State = {
  page: number,
  studentIdListCreated: boolean,
};

type Props = RequiredProps & GeneratedProps;

class SplitComposer extends React.Component<Props, State> {
  static defaultProps = {
    messageType: 'message',
  };

  constructor(props: Props) {
    super(props);
    const studentIdListRecipientInSelection = props.selection.some(
      s => s.type === RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT
    );

    this.state = {
      page: props.skipSelectRecipients ? 2 : 1,
      studentIdListCreated: studentIdListRecipientInSelection,
    };
  }

  componentDidMount() {
    const {
      initialTargets,
      onLoadOrganizations,
      onLoadGroups,
      onLoadGroupChats,
      onLoadIndividuals,
      onSearchGroupChats,
      onLoadPotentialChatMembers,
      organizations,
      groups,
    } = this.props;

    if (organizations) {
      onLoadOrganizations(organizations);
    }

    if (groups) {
      onLoadGroups(groups);
    }

    // Make sure we show available group chats.
    const groupChats = ChatStore.getGroupChats();
    if (groupChats.length === 0) {
      onSearchGroupChats('');
    } else {
      onLoadGroupChats(groupChats);
    }

    // Make sure we have some people to show.
    const individuals = ChatStore.getEligibleMemberModels();
    if (individuals.length === 0) {
      onLoadPotentialChatMembers('');
    } else {
      onLoadIndividuals(individuals);
    }

    if (initialTargets) {
      this.props.onSelectInitialTargets(initialTargets);
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const groupsWereNotPreviouslyLoaded = !prevProps.groups;
    const orgsWereNotPreviouslyLoaded = !prevProps.organizations;
    const prevGroupUuids = prevProps.groups ? prevProps.groups.map(group => group.uuid) : [];
    const newGroupUuids = this.props.groups ? this.props.groups.map(group => group.uuid) : [];
    const newGroupsWereLoaded = !isEqual(prevGroupUuids, newGroupUuids);
    const prevOrgUuids = prevProps.organizations
      ? prevProps.organizations.map(org => org.uuid)
      : [];
    const newOrgUuids = this.props.organizations
      ? this.props.organizations.map(org => org.uuid)
      : [];
    const newOrgsWereLoaded = !isEqual(prevOrgUuids, newOrgUuids);
    const { initialTargets } = this.props;

    if (this.props.groups && (groupsWereNotPreviouslyLoaded || newGroupsWereLoaded)) {
      this.props.onLoadGroups(this.props.groups);
      if (initialTargets) {
        this.props.onSelectInitialTargets(initialTargets);
      }
    }

    if (this.props.organizations && (orgsWereNotPreviouslyLoaded || newOrgsWereLoaded)) {
      this.props.onLoadOrganizations(this.props.organizations);
      if (initialTargets) {
        this.props.onSelectInitialTargets(initialTargets);
      }
    }

    const changed =
      prevProps.fileUploads.size !== this.props.fileUploads.size ||
      prevProps.savedMessage.length !== this.props.savedMessage.length ||
      prevState.studentIdListCreated !== this.state.studentIdListCreated ||
      (prevProps.selection.length !== this.props.selection.length && this.props.selection.length);

    if (changed) {
      if (this.props.onSignificantChangeMade) {
        this.props.onSignificantChangeMade();
      }
    }
  }

  componentWillUnmount() {
    this.removeAllFiles();
    this.props.onFlush();
  }

  advanceToComposeMessage = () => {
    this.setState({ page: 2 });
    if (this.props.onPageChange) {
      this.props.onPageChange('composer');
    }
  };

  backToSelectRecipients = () => {
    this.setState({ page: 1 });
    if (this.props.onPageChange) {
      this.props.onPageChange('share_to');
    }
  };

  handleSuccessfulStudentIdListCreation = () => {
    this.setState({ studentIdListCreated: true });
  };

  removeAllFiles = () => {
    const { fileUploads } = this.props;
    fileUploads
      .keySeq()
      .forEach(filename => this.props.removeFile(MODAL_FILE_CONTEXT_KEY, filename));
  };

  render() {
    const {
      onRequestHide,
      selection,
      initialTargets,
      draftAnnouncement,
      isEditingScheduledMessage,
      orgsLoading,
      schools,
      groupsLoading,
      loadingGroupChats,
      loadingIndividualRecipients,
      messageType,
    } = this.props;
    const { page } = this.state;

    const isUrgentMessage = messageType === 'urgent';

    const groupResultsAreLoading = orgsLoading || groupsLoading || loadingGroupChats;
    const initialTarget = idx(initialTargets, _ => _[0]);
    const groupTargetId: ?Backbone$Id =
      initialTarget && initialTarget.type === 'group_target' ? initialTarget.group_id : null;
    const orgTargetId: ?Backbone$Id =
      initialTarget && initialTarget.type === 'district_target' ? initialTarget.org_id : null;

    const renderComponentForPage = composerContext => {
      if (page === 1) {
        return (
          <SelectRecipientPanel
            onContinue={this.advanceToComposeMessage}
            loadingGroupResults={groupResultsAreLoading}
            loadingPeopleResults={loadingIndividualRecipients}
            schools={schools}
            selection={selection}
            onTabChange={this.props.clearSelection}
            onSuccessfulStudentIdListCreation={this.handleSuccessfulStudentIdListCreation}
            defaultTab={this.props.defaultTab}
            initialTargetId={groupTargetId || orgTargetId}
            messageType={messageType}
            isUrgentMessage={isUrgentMessage}
          />
        );
      } else if (page === 2) {
        return (
          <ComposeMessage
            initialTargets={initialTargets}
            draftAnnouncement={draftAnnouncement}
            onMessageSent={this.props.onMessageSent}
            isEditingScheduledMessage={isEditingScheduledMessage}
            onRequestHide={onRequestHide}
            onBack={this.backToSelectRecipients}
            shareSdkBodyUrl={this.props.shareSdkBodyUrl}
            isUrgentMessage={isUrgentMessage}
            UIEventContext={composerContext.getUIEventContext()}
          />
        );
      }

      return null;
    };

    return (
      <React.Fragment>
        <ComposerContext.Provider
          value={{
            isUrgentMessage,
            dataMessageType: 'message', // note this is default and should be changed here in the future
            // eslint-disable-next-line object-shorthand
            getUIEventContext: function() {
              return {
                message_type: this.dataMessageType,
                is_urgent: this.isUrgentMessage,
              };
            },
          }}>
          <SplitComposerHeader messageType={messageType} />
          <ComposerContext.Consumer>
            {composerContext => renderComponentForPage(composerContext)}
          </ComposerContext.Consumer>
        </ComposerContext.Provider>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: Object) => {
  const { roleBasedMessagingSearch, fileUploads } = state;
  const fileGroup = fileUploads.get(MODAL_FILE_CONTEXT_KEY);
  return {
    savedMessage: roleBasedMessagingSearch.message,
    fileUploads: fileGroup || new OrderedMap(),
    selection: roleBasedMessagingSearch.selection,
    loadingGroupChats: roleBasedMessagingSearch.loadingGroupChats,
    loadingIndividualRecipients: roleBasedMessagingSearch.loadingIndividualRecipients,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return bindActionCreators(
    {
      onFlush: flush,
      clearSelection,
      removeFile,
      onSelectInitialTargets: selectInitialTargets,
      onLoadGroups: loadGroups,
      onLoadOrganizations: loadOrganizations,
      onLoadGroupChats: loadGroupChats,
      onLoadIndividuals: loadIndividuals,
      onLoadPotentialChatMembers: loadPotentialChatMembers,
      onSearchGroupChats: searchGroupChats,
    },
    dispatch
  );
};

const withOrgs = graphql(COMPOSER_ORGS_QUERY, {
  props: ({ data }) => {
    const districts = idx(data, _ => _.me.organizations)
      ? data.me.organizations.filter(
          o => o.type === 'District' && o.permissions.canSendAnnouncement
        )
      : null;
    const schools = idx(data, _ => _.me.organizations)
      ? data.me.organizations.filter(o => o.type === 'School' && o.permissions.canSendAnnouncement)
      : null;

    return {
      orgsLoading: idx(data, _ => _.loading),
      organizations: districts,
      schools,
    };
  },
});

const withClasses = graphql(COMPOSER_GROUPS_QUERY, {
  props: ({ data }) => {
    return {
      groups: idx(data, _ => _.me.classes),
      groupsLoading: data.loading,
    };
  },
});

// we export this for the SplitComposerModal integration test
// because TargetByStudentIdUploadPage expects a DragDropContext
const SplitComposerWithDnD = DragDropContext(HTML5Backend)(SplitComposer);

export { SplitComposer, SplitComposerWithDnD };

export default (compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withClasses,
  withOrgs
)(SplitComposer): React$ComponentType<RequiredProps>);
